import {useEffect, useState} from 'react';

import {useGetDiscountLazyQuery} from '#apollo/schema';
import {Accordion, Button, Text, TextInput} from '#component/base';
import {PromoCodeAction} from '#component/page/Profile/components/ManagePlanTab/SubscriptionManager/SubscriptionManager.types';
import {useSubscriptionManager} from '#component/page/Profile/components/ManagePlanTab/SubscriptionManager/SubscriptionManagerContext';
import {AccordionRoot} from '#component/util';

import * as css from './PromoCodeField.css';

export const PromoCodeField = () => {
  const {appliedPromoCodes, updatePromoCodes, pendingChanges, discountsToSimulate} =
    useSubscriptionManager();

  const [promoCode, setPromoCode] = useState('');
  const [errorInvalidPromoCode, setErrorInvalidPromoCode] = useState<string | null>(null);
  const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);
  const [getDiscount, {loading}] = useGetDiscountLazyQuery({variables: {code: promoCode}});

  useEffect(() => {
    if (pendingChanges?.addDiscountCodes && pendingChanges.addDiscountCodes.length > 0) {
      setAccordionValue('promo-code');
    } else {
      setAccordionValue('');
    }
  }, [pendingChanges]);

  const handleChange = (value: string | undefined) => {
    setAccordionValue(value);
  };

  const handleApplyPromoCode = async () => {
    if (promoCode.trim() === '') {
      setErrorInvalidPromoCode('Please enter a promo code');
      return;
    }
    if (promoCode && discountsToSimulate.includes(promoCode)) {
      setErrorInvalidPromoCode(`'${promoCode.toUpperCase()}' has been used`);
    } else if (promoCode && !appliedPromoCodes.includes(promoCode)) {
      try {
        const {data} = await getDiscount({variables: {code: promoCode}});
        if (data?.getDiscount?.discountCode) {
          updatePromoCodes(promoCode, PromoCodeAction.ADD);
          setPromoCode('');
          setErrorInvalidPromoCode(null);
        } else {
          setErrorInvalidPromoCode(`'${promoCode.toUpperCase()}' is not a valid promo code`);
        }
      } catch (error) {
        setPromoCode('');
        setErrorInvalidPromoCode(`'${promoCode.toUpperCase()}' is not a valid promo code`);
      }
    }
  };

  const handleRemovePromoCode = (code: string) => {
    // This is done this way to remove the promo code from the list to simulate the discounts, including subscription discounts
    updatePromoCodes(code, PromoCodeAction.REMOVE);
  };

  return (
    <div className={css.PromoCodeField}>
      <AccordionRoot
        type="single"
        defaultValue="-1"
        value={accordionValue}
        onValueChange={handleChange}
        collapsible>
        <Accordion
          triggerClassName={css.AccordionTrigger}
          buttonClassName={css.AccordionButton}
          trigger={
            <>
              <Text className={css.Label} as="h4" variant="body" color="burgundy">
                {accordionValue === 'promo-code' ? 'Apply promo code' : 'Do you have a promo code?'}
              </Text>
            </>
          }
          value="promo-code">
          <div className={css.AccordionContentContainer}>
            <div className={css.AccordionContent}>
              <TextInput
                placeholder="Add discount code"
                value={promoCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPromoCode(e.target.value)}
              />
              <Button
                variant="secondary-dark"
                className={css.ApplyButton}
                loading={loading}
                onClick={handleApplyPromoCode}>
                Apply
              </Button>
            </div>
            {errorInvalidPromoCode && (
              <Text as="span" variant="caption" color="eggplant">
                {errorInvalidPromoCode}
              </Text>
            )}
            {appliedPromoCodes.length > 0 && (
              <div className={css.AppliedPromoCodes}>
                {appliedPromoCodes.map(code => (
                  <div key={code} className={css.AppliedPromoCode}>
                    <Text color="doveGray">{code}</Text>
                    <Button
                      variant="secondary"
                      className={css.AccordionButton}
                      icon="X"
                      onClick={() => handleRemovePromoCode(code)}
                    />
                  </div>
                ))}
              </div>
            )}
            {appliedPromoCodes.length > 0 && (
              <Text as="span" variant="caption" color="dustyGray">
                Updated price will be calculated on confirmation screen
              </Text>
            )}
          </div>
        </Accordion>
      </AccordionRoot>
    </div>
  );
};
